import React, { useState, useEffect, useCallback } from 'react';
import { MdSearch, MdEdit, MdDelete, MdPerson, MdSettings, MdCheckCircle, MdBlock, MdWork, MdMarkEmailRead, MdWarningAmber } from 'react-icons/md';
import { FiPackage, FiRefreshCw, FiFile, FiPlay, FiChevronLeft, FiChevronRight, FiArrowUp, FiArrowDown, FiDatabase, FiTag } from 'react-icons/fi';
import { BiSolidGift } from 'react-icons/bi';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import UserDetailsModal from '../components/UserDetailsModal';
import { apiService, getMediaUrl } from '../services/api';
import { User, Package, PromoCode, ConfirmEmailProfile } from '../types';

/**
 * Mirrors User.COMPANY_SIZE_CHOICES, AVG_JOBS_CHOICES and
 * AVG_SCREENINGS_CHOICES in BackEnd/core/models.py. The values must match
 * exactly - the serializer rejects anything else.
 */
const COMPANY_SIZE_OPTIONS = [
  { value: '1_9', label: '1-9' },
  { value: '10_49', label: '10-49' },
  { value: '50_249', label: '50-249' },
  { value: '250_500', label: '250-500' },
  { value: '500_plus', label: '500+' },
];

const AVG_JOBS_OPTIONS = [
  { value: '1_4', label: '1-4' },
  { value: '5_9', label: '5-9' },
  { value: '10_24', label: '10-24' },
  { value: '26_50', label: '26-50' },
  { value: '50_plus', label: '50+' },
];

const AVG_SCREENINGS_OPTIONS = [
  { value: '1_49', label: '1-49' },
  { value: '50_249', label: '50-249' },
  { value: '250_499', label: '250-499' },
  { value: '500_999', label: '500-999' },
  { value: '1000_plus', label: '1000+' },
];

const EMPTY_PROFILE: ConfirmEmailProfile = {
  organization: '',
  position: '',
  country_code: '',
  phone: '',
  city: '',
  company_size: '',
  avg_jobs_month: '',
  avg_screenings_month: '',
};

const verifyFieldClass =
  'w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Email verification bypass. Kept in its own state rather than reusing
  // selectedUser so opening it cannot disturb the package or details modal.
  const [verifyUser, setVerifyUser] = useState<User | null>(null);
  const [verifyWithProfile, setVerifyWithProfile] = useState(false);
  const [verifyForm, setVerifyForm] = useState<ConfirmEmailProfile>(EMPTY_PROFILE);
  const [verifySaving, setVerifySaving] = useState(false);

  // Format date to DD/MM/YYYY HH:MM
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return 'Invalid date';
    }
  };
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    organization: '',
  });
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'user'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'disabled'>('all');
  const [filterPromoCode, setFilterPromoCode] = useState<string>('all');
  const [filterVerified, setFilterVerified] = useState<'all' | 'verified' | 'unverified'>('all');
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [orderBy, setOrderBy] = useState<string>('-total_ai_usage'); // Default: sort by CVs analyzed descending
  const [sortDirection, setSortDirection] = useState<{ [key: string]: 'asc' | 'desc' }>({
    total_ai_usage: 'desc'
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'success';
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'danger', onConfirm: () => { } });
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [userForDetails, setUserForDetails] = useState<User | null>(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [packageForm, setPackageForm] = useState({
    package_id: 0,
    reset_usage: false,
    custom_monthly_ai_limit: null as number | null,
    custom_total_ai_limit: null as number | null,
    custom_storage_limit: null as number | null,
  });

  // Impersonation handler
  const handleImpersonateUser = async (userId: number, targetPage: 'my-jobs' | 'usage' | 'profile' | 'dashboard') => {
    try {
      const response = await apiService.impersonateUser(userId);

      if (response.success) {
        const { access_token, refresh_token, admin_id, admin_email } = response.data;

        // Store impersonation data in URL parameters
        const impersonationData = {
          access: access_token,
          refresh: refresh_token,
          adminId: admin_id,
          adminEmail: admin_email,
          adminReturn: window.location.href,
        };

        // Encode data as base64 for URL
        const encodedData = btoa(JSON.stringify(impersonationData));

        // Redirect to FrontEnd with impersonation token
        const frontendUrl = `https://app.teamther.ai/${targetPage}?impersonate=${encodedData}`;
        window.location.href = frontendUrl;
      }
    } catch (error: any) {
      setToast({ message: `Error impersonating user: ${error.message}`, type: 'error' });
    }
  };

  // Fetch users when filters/pagination change
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        page_size: pageSize,
        search: searchQuery,
        role: filterRole,
        status: filterStatus,
        order_by: orderBy,
      };
      
      // Add promo code filter if selected
      if (filterPromoCode && filterPromoCode !== 'all') {
        params.promo_code = filterPromoCode;
      }

      // Email verification filter
      if (filterVerified !== 'all') {
        params.verified = filterVerified;
      }
      
      const response = await apiService.getUsers(params);
      if (response && response.success && response.data) {
        setUsers(response.data.results);
        setTotalPages(response.data.total_pages);
        setTotalCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setToast({ message: 'Failed to load users. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, filterRole, filterStatus, orderBy, filterPromoCode, filterVerified]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchPackages();
    fetchPromoCodes();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to page 1 when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole, filterStatus, filterPromoCode, filterVerified, orderBy]);

  // Handle column sorting
  const handleSort = (column: string) => {
    const currentDirection = sortDirection[column] || 'asc';
    const newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
    const prefix = newDirection === 'desc' ? '-' : '';

    setSortDirection({ [column]: newDirection });
    setOrderBy(`${prefix}${column}`);
  };

  // Get sort icon for column
  const getSortIcon = (column: string) => {
    const direction = sortDirection[column];
    if (!direction) return null;
    return direction === 'asc' ? <FiArrowUp className="h-4 w-4" /> : <FiArrowDown className="h-4 w-4" />;
  };

  const fetchPackages = async () => {
    try {
      const response = await apiService.getPublicPackages();
      if (response.success) {
        setPackages(response.data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const response = await apiService.getPromoCodes();
      if (response.success) {
        setPromoCodes(response.data);
      }
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    }
  };


  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      organization: user.organization || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      await apiService.updateUser(selectedUser.id, editForm);
      setToast({ message: 'User updated successfully!', type: 'success' });
      setShowEditModal(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error updating user:', error);
      setToast({ message: error.message || 'Failed to update user.', type: 'error' });
    }
  };

  const handleToggleStatus = async (userId: number, currentStatus: boolean) => {
    const action = currentStatus ? 'disable' : 'enable';
    try {
      await apiService.enableDisableUser(userId, !currentStatus);
      setToast({ message: `User ${action}d successfully!`, type: 'success' });
      fetchUsers();
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      setToast({ message: error.message || `Failed to ${action} user.`, type: 'error' });
    }
  };

  const openVerifyModal = (user: User) => {
    setVerifyUser(user);
    setVerifyWithProfile(false);
    // Pre-fill whatever the user already answered, so the admin completes the
    // gaps instead of retyping the questionnaire.
    setVerifyForm({
      organization: user.organization || '',
      position: user.position || '',
      country_code: user.country_code || '',
      phone: user.phone || '',
      city: user.city || '',
      company_size: user.company_size || '',
      avg_jobs_month: user.avg_jobs_month || '',
      avg_screenings_month: user.avg_screenings_month || '',
    });
  };

  const handleConfirmEmail = async () => {
    if (!verifyUser) return;

    setVerifySaving(true);
    try {
      await apiService.confirmUserEmail(
        verifyUser.id,
        verifyWithProfile ? verifyForm : undefined
      );
      setToast({
        message: verifyWithProfile
          ? 'Email verified and profile completed.'
          : 'Email verified. The user completes their profile at next login.',
        type: 'success',
      });
      setVerifyUser(null);
      fetchUsers();
    } catch (error: any) {
      console.error('Error verifying user email:', error);
      setToast({ message: error.message || 'Failed to verify this email.', type: 'error' });
    } finally {
      setVerifySaving(false);
    }
  };

  // The backend requires all eight fields together, because a partial profile
  // leaves is_profile_complete false and the user is sent to the
  // questionnaire anyway.
  const verifyProfileIncomplete =
    verifyWithProfile && Object.values(verifyForm).some((value) => !value.trim());

  const handleDelete = (userId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete User',
      message: 'Are you sure you want to permanently delete this user? This action cannot be undone and will remove all their data.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await apiService.deleteUser(userId);
          setToast({ message: 'User deleted successfully!', type: 'success' });
          fetchUsers();
        } catch (error: any) {
          console.error('Error deleting user:', error);
          setToast({ message: error.message || 'Failed to delete user.', type: 'error' });
        }
      },
    });
  };

  const openPackageModal = (user: User) => {
    setSelectedUser(user);
    setPackageForm({
      package_id: user.package?.id || 0,
      reset_usage: false,
      custom_monthly_ai_limit: user.custom_monthly_ai_limit,
      custom_total_ai_limit: user.custom_total_ai_limit,
      custom_storage_limit: user.custom_storage_limit,
    });
    setShowPackageModal(true);
  };

  const handleUpdatePackage = async () => {
    if (!selectedUser || !packageForm.package_id) return;

    try {
      // Validate package ID exists
      const selectedPackage = packages.find(pkg => pkg.id === packageForm.package_id);
      if (!selectedPackage) {
        setToast({ message: 'Selected package is invalid.', type: 'error' });
        return;
      }

      // Update package first
      const packageResult = await apiService.updateUserPackage(
        selectedUser.id,
        packageForm.package_id,
        packageForm.reset_usage
      );

      if (!packageResult.success) {
        throw new Error(packageResult.error?.message || 'Package update failed');
      }

      // Update custom limits only if package update succeeded
      const limitsResult = await apiService.updateUserLimits(selectedUser.id, {
        custom_monthly_ai_limit: packageForm.custom_monthly_ai_limit,
        custom_total_ai_limit: packageForm.custom_total_ai_limit,
        custom_storage_limit: packageForm.custom_storage_limit,
      });

      if (!limitsResult.success) {
        // If limits update fails, warn but don't revert package change
        console.warn('Limits update failed:', limitsResult.error);
        setToast({
          message: `Package updated to ${selectedPackage.title}, but custom limits update failed. Please try updating limits manually.`,
          type: 'warning',
        });
      } else {
        setToast({
          message: `Package updated to ${selectedPackage.title} successfully!${packageForm.reset_usage ? ' Usage reset.' : ''}`,
          type: 'success',
        });
      }

      setShowPackageModal(false);
      fetchUsers(); // Refresh to show updated data including effective limits
    } catch (error: any) {
      console.error('Error updating package:', error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.message || 
                          'Failed to update package. Please check your inputs and try again.';
      setToast({ message: errorMessage, type: 'error' });
    }
  };

  // Stats are now based on total count from server
  const stats = {
    total: totalCount,
    admins: users.filter((u) => u.role === 'admin').length,
    active: users.filter((u) => u.is_enabled).length,
    disabled: users.filter((u) => !u.is_enabled).length,
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-navy-800 dark:text-white">
              Users Management
            </h2>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Users</p>
                <h3 className="mt-1 text-3xl font-bold">{stats.total}</h3>
              </div>
              <MdPerson className="h-12 w-12 opacity-80" />
            </div>
          </div>

          <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Admins</p>
                <h3 className="mt-1 text-3xl font-bold">{stats.admins}</h3>
              </div>
              <MdSettings className="h-12 w-12 opacity-80" />
            </div>
          </div>

          <div className="rounded-xl bg-gradient-to-br from-green-500 to-green-600 p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Active Users</p>
                <h3 className="mt-1 text-3xl font-bold">{stats.active}</h3>
              </div>
              <MdCheckCircle className="h-12 w-12 opacity-80" />
            </div>
          </div>

          <div className="rounded-xl bg-gradient-to-br from-red-500 to-red-600 p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Disabled</p>
                <h3 className="mt-1 text-3xl font-bold">{stats.disabled}</h3>
              </div>
              <MdBlock className="h-12 w-12 opacity-80" />
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col gap-4 md:flex-row">
          <div className="relative flex-1">
            <MdSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search users by name, email, or organization..."
              className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-12 pr-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>

            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value as any)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            >
              <option value="all">All Emails</option>
              <option value="verified">Verified</option>
              <option value="unverified">Not Verified</option>
            </select>

            <select
              value={filterPromoCode}
              onChange={(e) => setFilterPromoCode(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            >
              <option value="all">All Promo Codes</option>
              {promoCodes.map((promo) => (
                <option key={promo.id} value={promo.code}>
                  {promo.code} ({promo.user_count})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-hidden rounded-2xl bg-white shadow-lg dark:bg-navy-800">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-navy-700">
                <tr>
                  <th
                    onClick={() => handleSort('first_name')}
                    className="px-4 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition"
                  >
                    <div className="flex items-center gap-2">
                      User
                      {getSortIcon('first_name')}
                    </div>
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">
                    Package
                  </th>
                  <th
                    onClick={() => handleSort('total_jobs')}
                    className="px-4 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition"
                  >
                    <div className="flex items-center justify-center gap-2">
                      Jobs
                      {getSortIcon('total_jobs')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('total_cvs')}
                    className="px-4 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition"
                  >
                    <div className="flex items-center justify-center gap-2">
                      CVs
                      {getSortIcon('total_cvs')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('total_ai_usage')}
                    className="px-4 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition"
                  >
                    <div className="flex items-center justify-center gap-2">
                      Credits
                      {getSortIcon('total_ai_usage')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('current_storage_usage')}
                    className="px-4 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition"
                  >
                    <div className="flex items-center justify-center gap-2">
                      Storage
                      {getSortIcon('current_storage_usage')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('role')}
                    className="px-4 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition"
                  >
                    <div className="flex items-center gap-2">
                      Role
                      {getSortIcon('role')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('last_login')}
                    className="px-4 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition"
                  >
                    <div className="flex items-center gap-2">
                      Last Login
                      {getSortIcon('last_login')}
                    </div>
                  </th>
                  <th className="px-4 py-4 text-right text-sm font-semibold text-gray-600 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-navy-700">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center">
                      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr
                      key={user.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700"
                      onClick={() => {
                        setUserForDetails(user);
                        setShowUserDetails(true);
                      }}
                    >
                      {/* User Info */}
                      <td className="px-4 py-4">
                        <div
                          className="flex items-center space-x-3 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImpersonateUser(user.id, 'profile');
                          }}
                          title="Click to view profile as user"
                        >
                          {user.logo ? (
                            <img
                              src={getMediaUrl(user.logo) || ''}
                              alt={`${user.first_name} ${user.last_name}`}
                              className="h-10 w-10 rounded-full object-cover ring-2 ring-gray-200 dark:ring-navy-600"
                              onError={(e) => {
                                // Hide image and show initials fallback on error
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-600 text-white font-semibold ${user.logo ? 'hidden' : 'flex'
                              }`}
                          >
                            {user.first_name[0]?.toUpperCase()}
                            {user.last_name[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-navy-800 dark:text-white">
                              {user.first_name} {user.last_name}
                            </p>
                            <p className="font-medium text-navy-800 dark:text-white">
                              {user.email}
                            </p>
                            {!user.is_email_confirmed && (
                              <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                <MdWarningAmber className="h-3 w-3" />
                                Email not verified
                              </span>
                            )}
                            {user.promo_code_used && (
                              <div className="mt-1 flex items-center gap-1">
                                <BiSolidGift className="h-4 w-4 text-brand-500" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Package */}
                      <td className="px-4 py-4">
                        {user.package ? (
                          <div className="flex items-center gap-2">
                            <FiPackage className="h-4 w-4 text-brand-500" />
                            <span className="text-sm font-medium text-navy-700 dark:text-white">
                              {user.package.title}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No package</span>
                        )}
                      </td>
                      {/* Jobs */}
                      <td className="px-4 py-4 text-center">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            if ((user.stats?.total_jobs || 0) > 0) {
                              handleImpersonateUser(user.id, 'my-jobs');
                            }
                          }}
                          className={`inline-flex items-center gap-1 rounded-lg bg-purple-100 px-2 py-1 dark:bg-purple-900/20 ${(user.stats?.total_jobs || 0) > 0 ? 'cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/30 transition' : ''
                            }`}
                          title={(user.stats?.total_jobs || 0) > 0 ? 'Click to view jobs as user' : ''}
                        >
                          <MdWork className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                            {user.stats?.total_jobs || 0}
                          </span>
                        </div>
                      </td>
                      {/* CVs */}
                      <td className="px-4 py-4 text-center">
                        <div className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-2 py-1 dark:bg-blue-900/20">
                          <FiFile className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                            {user.stats?.total_cvs || 0}
                          </span>
                        </div>
                      </td>
                      {/* Runs - Progress Bar (Total CVs Analyzed) */}
                      <td className="px-4 py-4">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            if ((user.total_ai_usage || 0) > 0) {
                              handleImpersonateUser(user.id, 'usage');
                            }
                          }}
                          className={(user.total_ai_usage || 0) > 0 ? 'cursor-pointer' : ''}
                          title={(user.total_ai_usage || 0) > 0 ? 'Click to view usage as user' : ''}
                        >
                          {(() => {
                            const used = user.total_ai_usage || 0;
                            // Use effective limits that include subscription credits and custom limits
                            const limit = user.effective_limits?.total_ai_limit ?? user.custom_total_ai_limit ?? user.package?.total_transaction_ai ?? 0;
                            const percentage = limit > 0 ? Math.round((used / limit) * 100) : 0;

                            return (
                              <>
                                <div className="mb-1 flex items-center justify-end">
                                  <span className={`text-xs font-bold ${percentage > 95 ? 'text-red-600 dark:text-red-400' :
                                    percentage > 80 ? 'text-orange-600 dark:text-orange-400' :
                                      'text-gray-700 dark:text-gray-300'
                                    }`}>
                                    {percentage}%
                                  </span>
                                </div>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-600">
                                  <div
                                    className={`h-full rounded-full transition-all ${percentage > 95 ? 'bg-red-500' :
                                      percentage > 80 ? 'bg-orange-500' :
                                        'bg-green-500'
                                      }`}
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  ></div>
                                </div>
                                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                                  {used} / {limit === 0 ? '∞' : limit}
                                </p>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      {/* Storage - Progress Bar */}
                      <td className="px-4 py-4">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImpersonateUser(user.id, 'dashboard');
                          }}
                          className="cursor-pointer"
                          title="Click to view dashboard as user"
                        >
                          {(() => {
                            const used = user.stats?.storage_used_mb || 0;
                            // Use effective limits that include subscription credits and custom limits
                            const limit = user.effective_limits?.storage_limit ?? user.custom_storage_limit ?? user.package?.storage_mo ?? 0;
                            const percentage = limit > 0 ? Math.round((used / limit) * 100) : 0;

                            return (
                              <>
                                <div className="mb-1 flex items-center justify-end">
                                  <span className={`text-xs font-bold ${percentage > 95 ? 'text-red-600 dark:text-red-400' :
                                    percentage > 80 ? 'text-orange-600 dark:text-orange-400' :
                                      'text-gray-700 dark:text-gray-300'
                                    }`}>
                                    {percentage}%
                                  </span>
                                </div>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-600">
                                  <div
                                    className={`h-full rounded-full transition-all ${percentage > 95 ? 'bg-red-500' :
                                      percentage > 80 ? 'bg-orange-500' :
                                        'bg-blue-500'
                                      }`}
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  ></div>
                                </div>
                                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                                  {used.toFixed(2)} / {limit === 0 ? '∞' : limit} MB
                                </p>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      {/* Role */}
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${user.role === 'admin'
                            ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                            : 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                            }`}
                        >
                          {user.role}
                        </span>
                      </td>
                      {/* Last Login */}
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="font-medium text-navy-700 dark:text-white">
                          {formatDate(user.last_login)}
                        </div>
                        {(user.login_tracking?.login_count ?? 0) > 0 ? (
                          <div
                            className="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                            title="First recorded sign-in, and how many times this account has signed in"
                          >
                            First {formatDate(user.login_tracking?.first_login)}
                            {' \u00b7 '}
                            {user.login_tracking?.login_count}
                            {user.login_tracking?.login_count === 1 ? ' login' : ' logins'}
                          </div>
                        ) : (
                          <div
                            className="mt-0.5 text-xs font-medium text-amber-600 dark:text-amber-400"
                            title="No sign-in recorded in the activity log. The date above is the account creation, which also sets last_login."
                          >
                            Never signed in
                          </div>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-end gap-1">
                          {/* Verify Email Icon - only while the address is unverified */}
                          {!user.is_email_confirmed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openVerifyModal(user);
                              }}
                              className="rounded-lg p-2 text-amber-600 transition hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                              title="Mark email as verified"
                            >
                              <MdMarkEmailRead className="h-5 w-5" />
                            </button>
                          )}
                          {/* Edit Icon */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(user);
                            }}
                            className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            title="Edit User"
                          >
                            <MdEdit className="h-5 w-5" />
                          </button>
                          {/* Package Icon */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openPackageModal(user);
                            }}
                            className="rounded-lg p-2 text-purple-600 transition hover:bg-purple-50 dark:hover:bg-purple-900/20"
                            title="Manage Package"
                          >
                            <FiPackage className="h-5 w-5" />
                          </button>
                          {/* Toggle Switch and Delete stacked */}
                          <div className="flex flex-col items-center gap-1">
                            {/* Toggle Switch */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleStatus(user.id, user.is_enabled);
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${user.is_enabled ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                              title={user.is_enabled ? 'Enabled - Click to disable' : 'Disabled - Click to enable'}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${user.is_enabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                              />
                            </button>
                            {/* Delete Icon below toggle */}
                            {user.package && (parseFloat(user.package.monthly_price as any) === 0 || parseFloat(user.package.yearly_price as any) === 0) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(user.id);
                                }}
                                className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:hover:bg-red-900/20"
                                title="Delete User (Free Package Only)"
                              >
                                <MdDelete className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-navy-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
                >
                  <FiChevronLeft className="h-4 w-4" />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`h-9 w-9 rounded-lg text-sm font-medium transition ${currentPage === pageNum
                          ? 'bg-brand-500 text-white'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
                >
                  Next
                  <FiChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit User"
        size="md"
      >
        <div className="space-y-4">
          {/* First Name */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              First Name
            </label>
            <input
              type="text"
              value={editForm.first_name}
              onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Last Name
            </label>
            <input
              type="text"
              value={editForm.last_name}
              onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Email Address
            </label>
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Organization */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Organization
            </label>
            <input
              type="text"
              value={editForm.organization}
              onChange={(e) => setEditForm({ ...editForm, organization: e.target.value })}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          {/* Role */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Role
            </label>
            <select
              value={editForm.role}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value as any })}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Promo Code Used */}
          {selectedUser?.promo_code_used && (
            <div>
              <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                Promo Code Used
              </label>
              <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 dark:border-navy-600 dark:bg-navy-700">
                <BiSolidGift className="h-5 w-5 text-brand-500" />
                <span className="font-medium text-navy-700 dark:text-white">
                  {selectedUser.promo_code_used}
                </span>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setShowEditModal(false)}
              className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateUser}
              className="rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-2 font-medium text-white shadow-lg transition hover:from-brand-600 hover:to-brand-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.type === 'danger' ? 'Delete' : confirmDialog.type === 'warning' ? 'Disable' : 'Enable'}
      />

      {/* User Details Modal */}
      {userForDetails && (
        <UserDetailsModal
          isOpen={showUserDetails}
          onClose={() => setShowUserDetails(false)}
          user={userForDetails}
        />
      )}

      {/* Package Management Modal */}
      <Modal
        isOpen={showPackageModal}
        onClose={() => setShowPackageModal(false)}
        title="Manage User Package"
      >
        <div className="space-y-6">
          {selectedUser && (
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-600 text-lg font-semibold text-white">
                  {selectedUser.first_name[0]}{selectedUser.last_name[0]}
                </div>
                <div>
                  <p className="font-semibold text-navy-700 dark:text-white">
                    {selectedUser.first_name} {selectedUser.last_name}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedUser.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Package Info */}
          {selectedUser?.package && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/30 dark:bg-blue-900/10">
              <h4 className="mb-2 font-semibold text-blue-700 dark:text-blue-400">
                Current Package: {selectedUser.package.title}
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-300">
                <div>AI Usage: {selectedUser.current_month_ai_usage}/{selectedUser.package.monthly_limit_transaction_ai}</div>
                <div>Storage: {selectedUser.current_storage_usage}MB/{selectedUser.package.storage_mo}MB</div>
                <div>Total AI: {selectedUser.total_ai_usage}/{selectedUser.package.total_transaction_ai}</div>
              </div>
            </div>
          )}

          {/* Select New Package */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Package *
            </label>
            <select
              value={packageForm.package_id}
              onChange={(e) => setPackageForm({ ...packageForm, package_id: parseInt(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              required
            >
              <option value={0}>Select a package...</option>
              {packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.title} - ${pkg.monthly_price}/mo (AI: {pkg.monthly_limit_transaction_ai}/mo, Storage: {pkg.storage_mo}MB)
                </option>
              ))}
            </select>
          </div>

          {/* Custom Limits Section */}
          <div className="rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 p-4 dark:border-purple-900/30 dark:bg-purple-900/10">
            <div className="mb-3 flex items-center gap-2">
              <MdSettings className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <h4 className="font-semibold text-purple-700 dark:text-purple-400">
                ⚙️ Custom Limits (Optional)
              </h4>
            </div>
            <p className="mb-4 text-xs text-gray-600 dark:text-gray-400">
              Override package defaults for this user. Leave empty to use package limits.
            </p>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {/* Custom Monthly AI Limit */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Monthly AI Limit
                </label>
                <input
                  type="number"
                  min="0"
                  value={packageForm.custom_monthly_ai_limit || ''}
                  onChange={(e) => setPackageForm({
                    ...packageForm,
                    custom_monthly_ai_limit: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder={`Default: ${packages.find(p => p.id === packageForm.package_id)?.monthly_limit_transaction_ai || '—'}`}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>

              {/* Custom Total AI Limit */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Total AI Limit
                </label>
                <input
                  type="number"
                  min="0"
                  value={packageForm.custom_total_ai_limit || ''}
                  onChange={(e) => setPackageForm({
                    ...packageForm,
                    custom_total_ai_limit: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder={`Default: ${packages.find(p => p.id === packageForm.package_id)?.total_transaction_ai || '—'}`}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>

              {/* Custom Storage Limit */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Storage Limit (MB)
                </label>
                <input
                  type="number"
                  min="0"
                  value={packageForm.custom_storage_limit || ''}
                  onChange={(e) => setPackageForm({
                    ...packageForm,
                    custom_storage_limit: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder={`Default: ${packages.find(p => p.id === packageForm.package_id)?.storage_mo || '—'}MB`}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Reset Usage Option */}
          <div className="rounded-lg border-2 border-dashed border-orange-300 bg-orange-50 p-4 dark:border-orange-900/30 dark:bg-orange-900/10">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="reset_usage"
                checked={packageForm.reset_usage}
                onChange={(e) => setPackageForm({ ...packageForm, reset_usage: e.target.checked })}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              />
              <label htmlFor="reset_usage" className="flex-1 cursor-pointer">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  🔄 Reset Usage Stats
                </div>
                <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Reset monthly AI usage, total AI usage, and storage usage to 0. Use this when upgrading/downgrading packages.
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
            <button
              onClick={() => setShowPackageModal(false)}
              className="rounded-lg border border-gray-300 px-6 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdatePackage}
              disabled={!packageForm.package_id}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white shadow-lg transition hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiPackage className="h-5 w-5" />
              Update Package
            </button>
          </div>
        </div>
      </Modal>

      {/* Verify Email Modal */}
      <Modal
        isOpen={verifyUser !== null}
        onClose={() => setVerifyUser(null)}
        title="Verify email address"
        size={verifyWithProfile ? 'lg' : 'md'}
      >
        <div className="space-y-5">
          {verifyUser && (
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-600 text-lg font-semibold text-white">
                  {verifyUser.first_name[0]}{verifyUser.last_name[0]}
                </div>
                <div>
                  <p className="font-semibold text-navy-700 dark:text-white">
                    {verifyUser.first_name} {verifyUser.last_name}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {verifyUser.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/30 dark:bg-amber-900/10">
            <MdWarningAmber className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              This marks the address as verified without the user entering a code.
              Use it when the verification email never reached them.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Organization profile
            </p>

            <label
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${!verifyWithProfile
                ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/10'
                : 'border-gray-200 hover:bg-gray-50 dark:border-navy-600 dark:hover:bg-navy-700'
                }`}
            >
              <input
                type="radio"
                name="verify-profile-mode"
                checked={!verifyWithProfile}
                onChange={() => setVerifyWithProfile(false)}
                className="mt-1 h-4 w-4 accent-brand-500"
              />
              <span>
                <span className="block font-medium text-navy-700 dark:text-white">
                  The user fills it in
                </span>
                <span className="block text-sm text-gray-600 dark:text-gray-400">
                  They sign in and complete the questionnaire themselves, as usual.
                </span>
              </span>
            </label>

            <label
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${verifyWithProfile
                ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/10'
                : 'border-gray-200 hover:bg-gray-50 dark:border-navy-600 dark:hover:bg-navy-700'
                }`}
            >
              <input
                type="radio"
                name="verify-profile-mode"
                checked={verifyWithProfile}
                onChange={() => setVerifyWithProfile(true)}
                className="mt-1 h-4 w-4 accent-brand-500"
              />
              <span>
                <span className="block font-medium text-navy-700 dark:text-white">
                  I fill it in now
                </span>
                <span className="block text-sm text-gray-600 dark:text-gray-400">
                  The user signs in straight to the panel, with no questionnaire.
                </span>
              </span>
            </label>
          </div>

          {verifyWithProfile && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Organization *
                </label>
                <input
                  type="text"
                  value={verifyForm.organization}
                  onChange={(e) => setVerifyForm({ ...verifyForm, organization: e.target.value })}
                  className={verifyFieldClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Position *
                </label>
                <input
                  type="text"
                  value={verifyForm.position}
                  onChange={(e) => setVerifyForm({ ...verifyForm, position: e.target.value })}
                  className={verifyFieldClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  City *
                </label>
                <input
                  type="text"
                  value={verifyForm.city}
                  onChange={(e) => setVerifyForm({ ...verifyForm, city: e.target.value })}
                  className={verifyFieldClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Country code *
                </label>
                <input
                  type="text"
                  value={verifyForm.country_code}
                  onChange={(e) => setVerifyForm({ ...verifyForm, country_code: e.target.value })}
                  placeholder="FR"
                  className={verifyFieldClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Phone *
                </label>
                <input
                  type="text"
                  value={verifyForm.phone}
                  onChange={(e) => setVerifyForm({ ...verifyForm, phone: e.target.value })}
                  className={verifyFieldClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company size *
                </label>
                <select
                  value={verifyForm.company_size}
                  onChange={(e) => setVerifyForm({ ...verifyForm, company_size: e.target.value })}
                  className={verifyFieldClass}
                >
                  <option value="">Select...</option>
                  {COMPANY_SIZE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Jobs per month *
                </label>
                <select
                  value={verifyForm.avg_jobs_month}
                  onChange={(e) => setVerifyForm({ ...verifyForm, avg_jobs_month: e.target.value })}
                  className={verifyFieldClass}
                >
                  <option value="">Select...</option>
                  {AVG_JOBS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Screenings per month *
                </label>
                <select
                  value={verifyForm.avg_screenings_month}
                  onChange={(e) => setVerifyForm({ ...verifyForm, avg_screenings_month: e.target.value })}
                  className={verifyFieldClass}
                >
                  <option value="">Select...</option>
                  {AVG_SCREENINGS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
            <button
              onClick={() => setVerifyUser(null)}
              className="rounded-lg border border-gray-300 px-6 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmEmail}
              disabled={verifySaving || verifyProfileIncomplete}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white shadow-lg transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MdMarkEmailRead className="h-5 w-5" />
              {verifySaving ? 'Verifying...' : 'Verify email'}
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default Users;
